/**
 * Reviews Module
 *
 * Shop-level feedback/reviews.
 * Note: Shoppex has shop-level feedback, not product-level reviews.
 */

import { get } from '../core/client';
import { getConfig } from '../core/config';
import { buildEndpoint } from '../core/endpoint';
import type { SDKResponse, Feedback, ShopReviewsPage } from '../types';

const REVIEWS_PAGE_LIMIT = 100;
const REVIEWS_CACHE_TTL = 2 * 60 * 1000;

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function firstString(...values: unknown[]): string | undefined {
  for (const value of values) {
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return undefined;
}

function normalizeFeedback(raw: Feedback, index: number): Feedback {
  const record = raw as Feedback & Record<string, unknown>;
  const rating = toFiniteNumber(record.rating) ?? toFiniteNumber(record.score) ?? 0;
  const comment = firstString(record.comment, record.message);
  const author = firstString(record.author, record.customer_name);
  const createdAt = record.created_at ?? record.createdAt;
  const created_at =
    typeof createdAt === 'number' || typeof createdAt === 'string'
      ? String(createdAt)
      : '';
  const id = firstString(record.id, record.uniqid) ?? `review:${created_at || index}`;

  return {
    ...record,
    id,
    rating,
    ...(comment ? { comment } : {}),
    ...(author ? { author } : {}),
    created_at,
  };
}

export async function getShopReviewsPage(cursor?: string | null): Promise<SDKResponse<ShopReviewsPage>> {
  const config = getConfig();
  const query = new URLSearchParams();
  query.set('limit', String(REVIEWS_PAGE_LIMIT));
  if (typeof cursor === 'string' && cursor.trim().length > 0) {
    query.set('cursor', cursor);
  }
  const querySuffix = `?${query.toString()}`;

  const response = await get<ShopReviewsPage>(
    `${buildEndpoint('/v1/storefront/feedback/shop/:storeSlug', {
      storeSlug: config.storeSlug,
    })}${querySuffix}`,
    {
      cache: {
        key: `reviews:${config.storeSlug}:${cursor ?? 'start'}:${REVIEWS_PAGE_LIMIT}`,
        ttl: REVIEWS_CACHE_TTL,
        staleWhileRevalidate: true,
      },
    }
  );

  if (response.success && response.data) {
    return {
      success: true,
      data: {
        ...response.data,
        feedback: response.data.feedback.map(normalizeFeedback),
      },
    };
  }

  return {
    success: false,
    message: response.message,
  };
}

export async function getShopReviews(): Promise<SDKResponse<Feedback[]>> {
  const allFeedback: Feedback[] = [];
  const seenCursors = new Set<string>();
  let cursor: string | null = null;

  while (true) {
    const response = await getShopReviewsPage(cursor);
    if (!response.success || !response.data) {
      return {
        success: false,
        message: response.message,
        data: [],
      };
    }

    allFeedback.push(...response.data.feedback);

    const pagination = response.data.pagination;
    if (!pagination?.has_more || !pagination.next_cursor) {
      break;
    }

    if (seenCursors.has(pagination.next_cursor)) {
      break;
    }

    seenCursors.add(pagination.next_cursor);
    cursor = pagination.next_cursor;
  }

  return {
    success: true,
    data: allFeedback,
  };
}
